import React, { useState, useEffect, useCallback } from 'react';
import Login from './components/Login';
import Signup from './components/Signup';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Overview from './components/Overview';
import TestList from './components/TestList';
import TestSuiteManagement from './components/TestSuiteManagement';
import TestRuns from './components/TestRuns';
import Tasks from './components/Tasks';
import Reports from './components/Reports';
import CreateTestModal from './components/CreateTestModal';
import CreateApplicationModal from './components/CreateApplicationModal';
import AlertModal from './components/AlertModal';
import ConfirmModal from './components/ConfirmModal';
import RecordingView from './components/RecordingView';
import Settings from './components/Settings';
import Admin from './components/Admin';
import { API_BASE_URL, WS_BASE_URL, onAuthFailure } from './utils/api';
import './App.css';
import './components/Login/Loader.css';

// Keep the address bar in step with the page being shown.
//
// The app has no router: which page you see is held in React state, so the URL
// stayed on "/" whatever you clicked. That meant no bookmarking, no sharing a
// link to a page, and the browser Back button did nothing.
//
// This syncs the two using the browser's own history API - no extra library.
const VIEW_PATHS = {
  overview: '/dashboard',
  list: '/test-cases',
  suites: '/test-suites',
  runs: '/test-runs',
  tasks: '/scheduling',
  reports: '/reports',
  settings: '/settings',
  admin: '/admin',
  recording: '/record',
};

const PATH_VIEWS = Object.keys(VIEW_PATHS).reduce((acc, view) => {
  acc[VIEW_PATHS[view]] = view;
  return acc;
}, {});

function viewFromPath(pathname) {
  const clean = String(pathname || '/').replace(/\/+$/, '') || '/';
  return PATH_VIEWS[clean] || null;
}

function App() {
  const [isBooting, setIsBooting] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!localStorage.getItem('authToken');
  });
  const [avatarUrl, setAvatarUrl] = useState(() => {
    return localStorage.getItem('avatarUrl') || null;
  });
  const [authView, setAuthView] = useState('login'); // 'login' or 'signup'
  // Remembered so the Admin tab doesn't flicker away on every refresh while
  // /api/auth/me is in flight. The server re-states it on every call, and the
  // admin API routes check the database regardless of what's held here.
  const [role, setRole] = useState(() => localStorage.getItem('role') || 'member');
  const [view, setView] = useState(() => viewFromPath(window.location.pathname) || 'overview');
  const [ws, setWs] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showLaunchLoader, setShowLaunchLoader] = useState(false);
  const [showCreateApplicationModal, setShowCreateApplicationModal] = useState(false);
  const [editingApplication, setEditingApplication] = useState(null);
  const [currentTest, setCurrentTest] = useState(null);
  const [alertModal, setAlertModal] = useState({ isOpen: false, message: '', type: 'info' });
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  // Remembered across refreshes so the sidebar stays how the user left it.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('sidebarCollapsed') === 'true'
  );
  const [selectedApplication, setSelectedApplication] = useState(() => {
    // Try to load from localStorage
    const saved = localStorage.getItem('selectedApplication');
    return saved ? JSON.parse(saved) : null;
  });
  // TestList component now manages its own data fetching from database
  // No need to store tests in App.js state or localStorage

  useEffect(() => {
    console.log('[APP] API_BASE_URL:', API_BASE_URL);
    console.log('[APP] WS_BASE_URL:', WS_BASE_URL);
  }, []);

  const addLog = useCallback((message, type = 'info') => {
    console.log(`[${type}]`, message);
  }, []);

  const handleMessage = useCallback((data) => {
    switch (data.type) {
      case 'recordingStarted':
        addLog('Recording started', 'success');
        break;
      case 'recordingStopped':
        addLog('Recording stopped', 'info');
        // The browser is gone - stop pretending it is still starting up.
        setShowLaunchLoader(false);
        break;
      case 'testSaved':
        addLog(`Test saved: ${data.file}`, 'success');
        // TestList component will automatically reload from database
        break;
      case 'error':
        addLog(`Error: ${data.message}`, 'error');
        // "Launching browser..." only ever stopped on SUCCESS, so any failure
        // left it spinning for ever and the real error was never seen.
        setShowLaunchLoader(false);
        setAlertModal({ isOpen: true, type: 'error', message: data.message || 'Something went wrong' });
        break;
      default:
        break;
    }
  }, [addLog]);

  // Load user info on mount if authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    const token = localStorage.getItem('authToken');
    if (token) {
      fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(res => {
        // The session is genuinely gone (expired, or signed out elsewhere).
        // Without this the app stayed "logged in" with a dead token and every
        // request failed with 401 while the screen looked normal.
        if (res.status === 401) {
          localStorage.removeItem('authToken');
          setIsAuthenticated(false);
          return null;
        }
        return res.json();
      })
      .then(data => {
        if (!data) return;
        if (data.success && data.user) {
          if (data.user.avatarUrl) {
            localStorage.setItem('avatarUrl', data.user.avatarUrl);
            setAvatarUrl(data.user.avatarUrl);
          }
          if (data.user.username) {
            localStorage.setItem('username', data.user.username);
          }
          if (data.user.email) {
            localStorage.setItem('email', data.user.email);
          }
          // The server's answer wins over whatever the browser remembered.
          const serverRole = data.user.role || 'member';
          localStorage.setItem('role', serverRole);
          setRole(serverRole);
        }
      })
      .catch(err => {
        console.error('Error fetching user info:', err);
      });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    // The connection to the backend used to be made once and never remade. If it
    // dropped - which it always does when the backend restarts - the page looked
    // normal but every action went nowhere: no browser, no error, just a spinner.
    // Reconnect automatically instead.
    let closedOnPurpose = false;
    let retryTimer = null;
    let attempt = 0;
    let socket = null;

    // Keepalive, and a way to notice a connection that has silently died.
    //
    // The load balancer closes a connection that has been quiet for 60
    // seconds. It drops it without a close frame, so the browser never fires
    // onclose: readyState still says OPEN, every guard in the app still
    // passes, and ws.send() reports no error - the message simply goes
    // nowhere. The page looks fine and the buttons do nothing. Switching to
    // another Chrome tab and back was what made the browser finally notice
    // the dead socket and reconnect, which is why that "fixed" it.
    //
    // So: ping every 25 seconds (the connection is then never idle for 60),
    // and if the server has not answered for 70 seconds, stop believing the
    // socket and close it ourselves - onclose then runs the reconnect below.
    const PING_EVERY_MS = 25000;
    const SILENCE_LIMIT_MS = 70000;
    let heartbeat = null;
    let lastSeenAt = 0;

    const stopHeartbeat = () => {
      if (heartbeat) clearInterval(heartbeat);
      heartbeat = null;
    };

    const startHeartbeat = (sock) => {
      stopHeartbeat();
      lastSeenAt = Date.now();
      heartbeat = setInterval(() => {
        if (!sock || sock.readyState !== WebSocket.OPEN) return;

        if (Date.now() - lastSeenAt > SILENCE_LIMIT_MS) {
          addLog('Backend stopped responding - reconnecting', 'warning');
          stopHeartbeat();
          sock.close();   // triggers onclose, which reconnects
          return;
        }

        try {
          sock.send(JSON.stringify({ action: 'ping' }));
        } catch (error) {
          // Sending failed outright, so the socket is already gone.
          stopHeartbeat();
          sock.close();
        }
      }, PING_EVERY_MS);
    };

    const connect = () => {
      socket = new WebSocket(WS_BASE_URL);

      socket.onopen = () => {
        setIsConnected(true);
        addLog(attempt > 0 ? 'Reconnected to backend' : 'Connected to backend', 'info');
        attempt = 0;
        startHeartbeat(socket);
      };

      socket.onmessage = (event) => {
        // Any message at all proves the link is alive, not just a pong.
        lastSeenAt = Date.now();
        try {
          const parsed = JSON.parse(event.data);
          if (parsed && parsed.type === 'pong') return;   // keepalive only
          handleMessage(parsed);
        } catch (error) {
          addLog(`Error parsing message: ${error.message}`, 'error');
        }
      };

      socket.onerror = () => {
        addLog('WebSocket error', 'error');
      };

      socket.onclose = () => {
        setIsConnected(false);
        stopHeartbeat();
        if (closedOnPurpose) return;

        // Back off a little so a backend that is down does not get hammered,
        // but keep trying - a restart usually takes a few seconds.
        attempt += 1;
        const wait = Math.min(1000 * attempt, 5000);
        addLog(`Disconnected from backend - reconnecting in ${wait / 1000}s`, 'warning');
        retryTimer = setTimeout(connect, wait);
      };

      setWs(socket);
    };

    connect();

    return () => {
      closedOnPurpose = true;
      stopHeartbeat();
      if (retryTimer) clearTimeout(retryTimer);
      if (socket) socket.close();
    };
  }, [handleMessage, addLog, isAuthenticated]);

  const sendMessage = (action, payload = {}) => {
    console.log('[FRONTEND] sendMessage called:', { action, payload: { ...payload, content: payload.content ? `[${payload.content.length} chars]` : undefined } });
    
    if (!ws) {
      console.error('[FRONTEND] WebSocket is null');
      addLog('WebSocket not initialized. Please refresh the page.', 'error');
      return;
    }
    
    if (ws.readyState === WebSocket.CONNECTING) {
      console.log('[FRONTEND] WebSocket is connecting, waiting...');
      // Wait for connection
      ws.addEventListener('open', () => {
        console.log('[FRONTEND] WebSocket connected, sending message');
        ws.send(JSON.stringify({ action, payload }));
      }, { once: true });
      return;
    }
    
    if (ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ action, payload }));
        console.log('[FRONTEND] Message sent successfully');
      } catch (error) {
        console.error('[FRONTEND] Error sending message:', error);
        addLog(`Error sending message: ${error.message}`, 'error');
      }
    } else {
      console.error('[FRONTEND] WebSocket not connected. State:', ws.readyState);
      addLog(`WebSocket not connected (state: ${ws.readyState}). Please refresh the page.`, 'error');
    }
  };

  const handleCreateTest = () => {
    setShowCreateModal(true);
  };

  const handleCreateTestSubmit = async (formData) => {
    console.log('[APP] Create test submit:', {
      formData,
      selectedApplication,
      apiBase: API_BASE_URL
    });

    setShowCreateModal(false);
    setShowLaunchLoader(true);

    // Create the test case in the DB first so the recorder gets a real ID
    let newTest;
    try {
      const res = await fetch(`${API_BASE_URL}/api/test-cases`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          application_id: selectedApplication?.id || null,
          name: formData.testName || `Test ${Date.now()}`,
          content: `import { test, expect } from '@playwright/test';\n\ntest.use({ viewport: null, launchOptions: { args: ['--start-maximized'] } }); // open maximised; the page fills the window\n\nconst td = require('./helpers/testdata'); // resolves {{email}} style values at run time\n\ntest('${formData.testName || 'recorded test'}', async ({ page }) => {\n  test.setTimeout(120000);\n  page.setDefaultTimeout(30000);\n});\n`,
          url: formData.startUrl || null,
          steps: [],
        }),
      });
      const data = await res.json();
      if (data.success && data.testCase) {
        console.log('[APP] Test case created in DB, id:', data.testCase.id);
        newTest = {
          ...data.testCase,
          url: formData.startUrl || data.testCase.url,
          tunnel: formData.tunnel,
          autoLogin: formData.autoLogin,
        };
      } else {
        throw new Error(data.error || 'Failed to create test case');
      }
    } catch (err) {
      console.error('[APP] Failed to pre-create test case:', err.message);
      // Fall back to local object so the user can still record
      newTest = {
        id: Date.now(),
        name: formData.testName || `Test ${Date.now()}`,
        url: formData.startUrl,
        tunnel: formData.tunnel,
        autoLogin: formData.autoLogin,
      };
    }

    setCurrentTest(newTest);
    setView('recording');
  };

  const handleSelectTest = (test) => {
    setCurrentTest(test);
    setShowLaunchLoader(true); // Show launch loader when editing a test
    setView('recording');
  };

  const handleRunTest = (test) => {
    // Navigate to recording view in execution mode
    setCurrentTest({ ...test, executionMode: true });
    setView('recording');
  };

  const handleBackToList = () => {
    setView('list');
    setCurrentTest(null);
    setShowLaunchLoader(false); // Reset loader when going back
  };

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleSignup = () => {
    setIsAuthenticated(true);
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sidebarCollapsed', String(next));
      return next;
    });
  };

  const handleLogout = () => {
    setShowLogoutConfirm(false);
    localStorage.removeItem('authToken');
    localStorage.removeItem('username');
    localStorage.removeItem('email');
    localStorage.removeItem('avatarUrl');
    localStorage.removeItem('role');
    setRole('member');
    setIsAuthenticated(false);
    setView('list');
    setCurrentTest(null);
  };

  // The fetch wrapper in utils/api.js shouts when the backend rejects the
  // token, so a dead session drops back to the login screen instead of leaving
  // a normal-looking page where nothing works.
  useEffect(() => onAuthFailure(() => {
    setIsAuthenticated(false);
    setRole('member');
  }), []);

  // Last resort for the "Launching browser..." banner. It is switched off by a
  // specific event, and every time one of those paths changed it risked being
  // left on screen for ever. Give it a hard ceiling instead.
  useEffect(() => {
    if (!showLaunchLoader) return undefined;
    const timer = setTimeout(() => {
      console.warn('[APP] Launch banner timed out - clearing it.');
      setShowLaunchLoader(false);
    }, 30000);
    return () => clearTimeout(timer);
  }, [showLaunchLoader]);

  // Address bar follows the page.
  useEffect(() => {
    if (!isAuthenticated) return;
    const path = VIEW_PATHS[view] || '/dashboard';
    if (window.location.pathname !== path) {
      window.history.pushState({ view }, '', path);
    }
  }, [view, isAuthenticated]);

  // Back and Forward move between pages instead of leaving the app.
  useEffect(() => {
    const handlePopState = () => {
      const next = viewFromPath(window.location.pathname);
      if (!next) return;
      // The recorder needs a test to record; going Back to it without one
      // would show an empty recorder, so fall back to the test list.
      setView(next === 'recording' && !currentTest ? 'list' : next);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentTest]);

  useEffect(() => {
    const timer = setTimeout(() => setIsBooting(false), 700);
    return () => clearTimeout(timer);
  }, []);

  // Memoised deliberately, not as a tidy-up - and it has to live up here,
  // above the `if (!isAuthenticated)` return below, because a hook cannot be
  // called after a conditional return.
  //
  // The Sidebar lists this in the dependencies of the effect that fetches the
  // applications. Declared plainly, it became a NEW function on every render of
  // App, so that effect saw a changed dependency every time and fetched the list
  // again - which set state, which re-rendered App, which made another new
  // function. The network tab showed /api/applications three or more times on
  // one page load, with every other request repeating behind it, and the test
  // list sat empty until the churn settled on an application.
  const handleApplicationChange = useCallback((application) => {
    setSelectedApplication(application);
    localStorage.setItem('selectedApplication', JSON.stringify(application));
    // Reload current view to show data for new application
    // This will trigger re-renders in child components
  }, []);

  if (!isAuthenticated) {
    if (authView === 'signup') {
      return (
        <Signup 
          onSignup={handleSignup} 
          onSwitchToLogin={() => setAuthView('login')}
        />
      );
    }
    if (isBooting) {
      return (
        <div className="tf-loader-overlay">
          <div className="tf-loader">
            <div className="tf-badge">TF</div>
            <div className="tf-spinner" />
          </div>
        </div>
      );
    }
    return (
      <Login 
        onLogin={handleLogin}
        onSwitchToSignup={() => setAuthView('signup')}
      />
    );
  }

  const handleSidebarNavigate = (viewType) => {
    if (viewType === 'overview') {
      setView('overview');
      setCurrentTest(null);
    } else if (viewType === 'list') {
      setView('list');
      setCurrentTest(null);
    } else if (viewType === 'suites') {
      setView('suites');
      setCurrentTest(null);
    } else if (viewType === 'runs') {
      setView('runs');
      setCurrentTest(null);
    } else if (viewType === 'tasks') {
      setView('tasks');
      setCurrentTest(null);
    } else if (viewType === 'reports') {
      setView('reports');
      setCurrentTest(null);
    } else if (viewType === 'defects') {
      setView('defects');
      setCurrentTest(null);
    } else if (viewType === 'settings') {
      setView('settings');
      setCurrentTest(null);
    } else if (viewType === 'admin') {
      setView('admin');
      setCurrentTest(null);
    }
  };

  const handleCreateTestFromAI = (testData) => {
    // Navigate to recording view with AI-generated test
    // The test is already saved in the database, so we just need to open it
    setCurrentTest({
      id: testData.id,
      name: testData.name,
      url: testData.url,
      content: testData.content,
      steps: testData.steps,
      aiGenerated: true
    });
    setView('recording');
  };


  const handleCreateApplication = () => {
    setEditingApplication(null);
    setShowCreateApplicationModal(true);
  };

  const handleEditApplication = (application) => {
    setEditingApplication(application);
    setShowCreateApplicationModal(true);
  };

  const handleCreateApplicationSubmit = async (formData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Select the newly created application
        handleApplicationChange(data.application);
        setShowCreateApplicationModal(false);
        setEditingApplication(null);
      } else {
        setAlertModal({ isOpen: true, message: `Error creating application: ${data.error}`, type: 'error' });
      }
    } catch (error) {
      console.error('Error creating application:', error);
      setAlertModal({ isOpen: true, message: `Error creating application: ${error.message}`, type: 'error' });
    }
  };

  const handleUpdateApplication = async (applicationId, formData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/applications/${applicationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Update the selected application if it's the one being edited
        if (selectedApplication && selectedApplication.id === applicationId) {
          handleApplicationChange(data.application);
        }
        setShowCreateApplicationModal(false);
        setEditingApplication(null);
      } else {
        setAlertModal({ isOpen: true, message: `Error updating application: ${data.error}`, type: 'error' });
      }
    } catch (error) {
      console.error('Error updating application:', error);
      setAlertModal({ isOpen: true, message: `Error updating application: ${error.message}`, type: 'error' });
    }
  };

  const showSidebar = view !== 'recording';

  return (
    <div className="App">
      {isBooting && (
        <div className="tf-loader-overlay">
          <div className="tf-loader">
            <div className="tf-badge">TF</div>
            <div className="tf-spinner" />
          </div>
        </div>
      )}
      {showSidebar && (
        <Sidebar 
          currentView={view}
          onNavigate={handleSidebarNavigate}
          username={localStorage.getItem('username') || 'User'}
          avatarUrl={avatarUrl}
          onLogout={() => setShowLogoutConfirm(true)}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={toggleSidebar}
          selectedApplication={selectedApplication}
          onApplicationChange={handleApplicationChange}
          onCreateApplication={handleCreateApplication}
          onEditApplication={handleEditApplication}
          isAdmin={role === 'admin'}
        />
      )}
      <div className={`main-content ${!showSidebar ? 'full-width' : ''} ${showSidebar && sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {showSidebar && (
          <Header 
            username={localStorage.getItem('username') || 'User'}
            currentView={view}
          />
        )}
        {showLaunchLoader && (
          <div className="launch-progress">
            <div className="launch-progress-track">
              <div className="launch-progress-fill" />
            </div>
            <span>Launching browser...</span>
          </div>
        )}
        {view === 'overview' ? (
          <Overview
            onCreateTest={handleCreateTest}
            onNavigateToTests={() => setView('list')}
            onNavigateToSuites={() => setView('suites')}
            onNavigateToScheduling={() => setView('tasks')}
            onNavigateToRuns={() => setView('runs')}
            onRunTest={handleRunTest}
            onSelectTest={handleSelectTest}
            selectedApplication={selectedApplication}
            username={localStorage.getItem('username') || 'User'}
          />
        ) : view === 'list' ? (
          <TestList
            onSelectTest={handleSelectTest}
            onCreateTest={handleCreateTest}
            ws={ws}
            isConnected={isConnected}
            sendMessage={sendMessage}
            onRunTest={handleRunTest}
            selectedApplication={selectedApplication}
          />
        ) : view === 'suites' ? (
          <TestSuiteManagement 
            onSelectTest={handleSelectTest}
            selectedApplication={selectedApplication}
          />
        ) : view === 'runs' ? (
          <TestRuns 
            selectedApplication={selectedApplication}
            ws={ws}
            isConnected={isConnected}
          />
        ) : view === 'tasks' ? (
          <Tasks selectedApplication={selectedApplication} />
        ) : view === 'reports' ? (
          <Reports 
            selectedApplication={selectedApplication}
            ws={ws}
            isConnected={isConnected}
            sendMessage={sendMessage}
            onRunTest={handleRunTest}
            onSelectTest={handleSelectTest}
          />
        ) : view === 'settings' ? (
          <Settings
            onCreateTestFromAI={handleCreateTestFromAI}
            selectedApplication={selectedApplication}
          />
        ) : view === 'admin' ? (
          // Guarded here as well as in the sidebar, so typing /admin in the
          // address bar doesn't open the screen. The real check is on the
          // server - every call this screen makes is refused for a non-admin.
          role === 'admin' ? <Admin /> : (
            <div style={{ padding: '2rem', color: '#6B7280' }}>
              You do not have access to this page.
            </div>
          )
        ) : (
          <RecordingView
            test={currentTest}
            onBack={handleBackToList}
            ws={ws}
            isConnected={isConnected}
            sendMessage={sendMessage}
            selectedApplication={selectedApplication}
            onBrowserReady={() => setShowLaunchLoader(false)}
          />
        )}
      </div>

      {/* These modals live outside the view switch on purpose. They used to sit
          inside the `view === 'list'` branch, which meant opening one from the
          Dashboard (or any other page) set the flag but rendered nothing - the
          button looked dead. Anything triggerable from more than one page has
          to be mounted at the top level. */}
      <CreateTestModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateTestSubmit}
      />
      <CreateApplicationModal
        isOpen={showCreateApplicationModal}
        onClose={() => {
          setShowCreateApplicationModal(false);
          setEditingApplication(null);
        }}
        onCreate={handleCreateApplicationSubmit}
        onUpdate={handleUpdateApplication}
        editingApplication={editingApplication}
      />
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ isOpen: false, message: '', type: 'info' })}
      />
      <ConfirmModal
        isOpen={showLogoutConfirm}
        title="Log out"
        message="Are you sure you want to log out of TestFlow?"
        confirmText="Log out"
        cancelText="Cancel"
        type="danger"
        onConfirm={handleLogout}
        onCancel={() => setShowLogoutConfirm(false)}
      />
    </div>
  );
}

export default App;
